import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Eye, EyeOff, AlertTriangle, Lock, Unlock, RefreshCw, Truck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL, ImageUpload, refreshSuppliersCache } from './shared';

const EMPTY_FORM = {
  name: '', logo_url: '', contact_phone: '', contact_email: '', address: '', notes: '', status: 'active',
};

const STATUS_META = {
  active:   { label: 'Active',   className: 'bg-green-100 text-green-700' },
  hidden:   { label: 'Hidden',   className: 'bg-yellow-100 text-yellow-700' },
  disabled: { label: 'Disabled', className: 'bg-red-100 text-red-700' },
  deleted:  { label: 'Deleted',  className: 'bg-gray-200 text-gray-600' },
};

const SuppliersTab = ({ token }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteMode, setDeleteMode] = useState('archive');
  const [reassignTo, setReassignTo] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSuppliers = async () => {
    setLoading(true);
    refreshSuppliersCache();
    try {
      const res = await axios.get(`${API_URL}/admin/suppliers?include_deleted=${includeDeleted}`, { headers });
      setSuppliers(res.data || []);
    } catch (e) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, [includeDeleted]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowFormModal(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      logo_url: s.logo_url || '',
      contact_phone: s.contact_phone || '',
      contact_email: s.contact_email || '',
      address: s.address || '',
      notes: s.notes || '',
      status: s.status || 'active',
    });
    setShowFormModal(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API_URL}/admin/suppliers/${editing.id}`, form, { headers });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API_URL}/admin/suppliers`, form, { headers });
        toast.success('Supplier created');
      }
      setShowFormModal(false);
      fetchSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const setStatus = async (s, newStatus) => {
    const label = { hidden: 'hide', disabled: 'disable', active: 'restore' }[newStatus] || 'update';
    if (!window.confirm(`Are you sure you want to ${label} "${s.name}"? ${
      newStatus === 'hidden' ? 'It will be removed from all public surfaces (products, courses, events, trips associated will also be hidden).' :
      newStatus === 'disabled' ? 'It will be hidden publicly AND locked against new product creation / imports.' :
      'It will become visible again on the public site.'
    }`)) return;
    try {
      await axios.post(`${API_URL}/admin/suppliers/${s.id}/status`, { status: newStatus }, { headers });
      toast.success(`Supplier ${label}d`);
      fetchSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const openDelete = (s) => {
    setDeleteTarget(s);
    setDeleteMode('archive');
    setReassignTo('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteMode === 'reassign' && !reassignTo) {
      toast.error('Choose a supplier to reassign to');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/admin/suppliers/${deleteTarget.id}/delete`,
        { mode: deleteMode, reassign_to_id: reassignTo || null },
        { headers },
      );
      toast.success('Supplier deleted');
      setShowDeleteModal(false);
      fetchSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const counts = (s) => s.affected_counts || { products: 0, brand_products: 0, courses: 0, events: 0, trips: 0 };
  const totalCount = (s) => Object.values(counts(s)).reduce((a, b) => a + b, 0);

  const reassignOptions = suppliers.filter(
    (x) => deleteTarget && x.id !== deleteTarget.id && x.status !== 'deleted',
  );

  return (
    <div data-testid="suppliers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Truck className="w-6 h-6 text-[#FF6B35]" />
          <h2 className="text-lg font-semibold">Suppliers ({suppliers.length})</h2>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              data-testid="show-deleted-suppliers-checkbox"
            />
            Show deleted
          </label>
          <Button onClick={fetchSuppliers} variant="outline" size="sm" data-testid="refresh-suppliers-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={openCreate} data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Supplier
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4 max-w-2xl">
        Suppliers (e.g. Aqua Divers, Manex &amp; Power Marine, Scuba Crew) provide products, courses, events &amp; trips to Just Dive. Use <strong>Hide</strong> to temporarily remove everything they supply from the public site, <strong>Disable</strong> to also lock against new product creation, and <strong>Delete</strong> for permanent removal.
      </p>

      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0A2342] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm">Supplier</th>
                <th className="px-4 py-3 text-left text-sm">Contact</th>
                <th className="px-4 py-3 text-center text-sm">Items</th>
                <th className="px-4 py-3 text-center text-sm">Status</th>
                <th className="px-4 py-3 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : suppliers.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No suppliers yet. Click "Add Supplier" to create one.</td></tr>
              ) : suppliers.map((s) => {
                const meta = STATUS_META[s.status] || STATUS_META.active;
                const c = counts(s);
                return (
                  <tr key={s.id} className="border-b hover:bg-gray-50" data-testid={`supplier-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {s.logo_url && <img src={s.logo_url} alt="" className="w-10 h-10 object-cover rounded" />}
                        <div>
                          <div className="font-medium text-sm" data-testid={`supplier-name-${s.id}`}>{s.name}</div>
                          {s.address && <div className="text-xs text-gray-500 truncate max-w-xs">{s.address}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {s.contact_phone && <div className="text-gray-700">{s.contact_phone}</div>}
                      {s.contact_email && <div className="text-gray-500 text-xs">{s.contact_email}</div>}
                      {!s.contact_phone && !s.contact_email && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-center" title={`${c.products} products · ${c.brand_products} brand products · ${c.courses} courses · ${c.events} events · ${c.trips} trips`}>
                      <span className="font-semibold text-[#0A2342]">{totalCount(s)}</span>
                      <div className="text-xs text-gray-500">items</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${meta.className}`} data-testid={`supplier-status-${s.id}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1 flex-wrap">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(s)} title="Edit" data-testid={`edit-supplier-${s.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {s.status === 'active' && (
                          <>
                            <Button size="sm" variant="ghost" className="text-yellow-600" onClick={() => setStatus(s, 'hidden')} title="Hide from public" data-testid={`hide-supplier-${s.id}`}>
                              <EyeOff className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-orange-600" onClick={() => setStatus(s, 'disabled')} title="Disable (hide + lock new products)" data-testid={`disable-supplier-${s.id}`}>
                              <Lock className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(s.status === 'hidden' || s.status === 'disabled') && (
                          <Button size="sm" variant="ghost" className="text-green-600" onClick={() => setStatus(s, 'active')} title="Restore" data-testid={`restore-supplier-${s.id}`}>
                            {s.status === 'disabled' ? <Unlock className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        )}
                        {s.status !== 'deleted' && (
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => openDelete(s)} title="Delete" data-testid={`delete-supplier-${s.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Supplier Name (e.g. Aqua Divers)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" />
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="Phone (e.g. +2712 880 5300)" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="supplier-phone-input" />
              <Input placeholder="Email (optional)" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <Input placeholder="Address (optional)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <textarea className="w-full border rounded-md p-2 text-sm" rows={2} placeholder="Internal notes (not shown publicly)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo (optional)</label>
              <ImageUpload value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} token={token} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFormModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="supplier-submit-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete Supplier: {deleteTarget?.name}
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                <p className="font-semibold text-red-700 mb-2">The following items reference this supplier:</p>
                <ul className="text-red-700 ml-4 list-disc space-y-0.5">
                  <li>{counts(deleteTarget).products} products</li>
                  <li>{counts(deleteTarget).brand_products} brand products</li>
                  <li>{counts(deleteTarget).courses} courses</li>
                  <li>{counts(deleteTarget).events} events</li>
                  <li>{counts(deleteTarget).trips} trips</li>
                </ul>
                <p className="text-xs text-red-600 mt-2 italic">Historical orders preserve their snapshot and are not modified.</p>
              </div>
              <div className="space-y-2">
                <label className="block font-medium text-[#0A2342]">What should happen to those items?</label>
                <label className="flex items-start gap-2 p-3 rounded border cursor-pointer hover:bg-gray-50" data-testid="delete-mode-archive">
                  <input type="radio" name="delete_mode" value="archive" checked={deleteMode === 'archive'} onChange={() => setDeleteMode('archive')} className="mt-1" />
                  <div className="text-sm">
                    <div className="font-semibold">Archive items (recommended)</div>
                    <div className="text-gray-600 text-xs">Hide all linked items (mark as out of stock + archived) but keep data for reporting.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 rounded border cursor-pointer hover:bg-gray-50" data-testid="delete-mode-reassign">
                  <input type="radio" name="delete_mode" value="reassign" checked={deleteMode === 'reassign'} onChange={() => setDeleteMode('reassign')} className="mt-1" />
                  <div className="text-sm flex-1">
                    <div className="font-semibold">Reassign to another supplier</div>
                    <div className="text-gray-600 text-xs mb-2">Move every linked item to a different supplier.</div>
                    {deleteMode === 'reassign' && (
                      <select className="border rounded px-2 py-1 text-sm w-full" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} data-testid="reassign-to-select">
                        <option value="">— Choose supplier —</option>
                        {reassignOptions.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </select>
                    )}
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 rounded border cursor-pointer hover:bg-gray-50" data-testid="delete-mode-delete-products">
                  <input type="radio" name="delete_mode" value="delete_products" checked={deleteMode === 'delete_products'} onChange={() => setDeleteMode('delete_products')} className="mt-1" />
                  <div className="text-sm">
                    <div className="font-semibold text-red-700">Permanently delete all linked items</div>
                    <div className="text-gray-600 text-xs">⚠️ Cannot be undone. Removes products, brand products, courses, events &amp; trips outright.</div>
                  </div>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete} data-testid="confirm-delete-supplier-btn">Confirm Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersTab;
